import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Upload, Layers, Download, Sparkles, ScanLine, CircleCheck, Info } from "lucide-react";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { BrandExtrasSection } from "@/components/BrandExtrasSection";
import { CustomLogoBanner } from "@/components/CustomLogoBanner";
import { PricingSection } from "@/components/PricingSection";
import { AiLogoExample } from "@/components/AiLogoExample";
import { SketchShowcase } from "@/components/SketchShowcase";
import { FaqSection } from "@/components/FaqSection";

export default function Landing() {
  const { user } = useAuth();
  const startPath = user ? "/dashboard" : "/register";
  return (
    <div className="min-h-screen bg-white text-[#0A0A0B]" data-testid="landing-page">
      <Header />

      {/* HERO ----------------------------------------------------------- */}
      <section className="relative overflow-hidden border-b border-black/5">
        <img
          src="https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNoYXBlc3xlbnwwfHx8fDE3ODE2NzE1OTh8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.06] pointer-events-none"
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 md:pt-32 md:pb-40">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-px bg-[#0A0A0B]" />
                <span className="text-xs uppercase tracking-[0.3em] text-[#52525B] font-bold">
                  Vector Conversion Engine — v1.0
                </span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-black mb-8" data-testid="hero-title">
                Pixel logos in.
                <br />
                <span className="bg-gradient-to-r from-[#ED5A24] to-[#1F8FED] bg-clip-text text-transparent">Clean vectors</span> out.
              </h1>
              <p className="text-base sm:text-lg text-[#52525B] max-w-2xl leading-relaxed mb-10" data-testid="hero-subtitle">
                Made your logo with AI but don't have editable artwork files? Upload the
                low-res image and get crisp, scalable artwork in SVG, EPS, AI, PDF and
                high-resolution PNG/JPG — ready for print, signage and your designer.
              </p>
              <div
                className="inline-flex items-start gap-2.5 border-l-2 border-[#ED5A24] bg-[#ED5A24]/5 px-4 py-3 mb-10 -mt-4 max-w-2xl text-sm text-[#0A0A0B]"
                data-testid="hero-2d-note"
              >
                <Info className="w-4 h-4 text-[#ED5A24] mt-0.5 shrink-0" strokeWidth={2.5} />
                <span>
                  <strong>2D logos only.</strong> Built for flat logos and wordmarks — 3D, photographic or heavily
                  shaded artwork won't convert cleanly.
                </span>
              </div>
              <div className="flex flex-wrap gap-3" data-testid="hero-cta-group">
                <Link to={startPath}>
                  <Button
                    size="lg"
                    data-testid="hero-cta-primary"
                    className="rounded-none bg-gradient-to-r from-[#ED5A24] to-[#1F8FED] hover:from-[#C44619] hover:to-[#0F6FBF] text-white text-xs uppercase tracking-[0.2em] font-bold h-12 px-8"
                  >
                    Start vectorizing <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                {!user && <Link to="/login">
                  <Button
                    size="lg"
                    variant="outline"
                    data-testid="hero-cta-secondary"
                    className="rounded-none border-[#0A0A0B] hover:bg-[#0A0A0B] hover:text-white text-xs uppercase tracking-[0.2em] font-bold h-12 px-8"
                  >
                    I have an account
                  </Button>
                </Link>}
              </div>

              <div className="mt-16 grid grid-cols-3 gap-6 max-w-xl">
                <Stat number="6" label="Output formats" />
                <Stat number="16" label="Max colors" />
                <Stat number="2048" label="HD output px" />
              </div>
            </div>

            <div className="md:col-span-4 mt-12 md:mt-0">
              <div className="border border-black/10 bg-[#F8F9FA] p-6 md:p-8">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#52525B] mb-4">Formats included</div>
                <ul className="space-y-3 text-sm">
                  {["SVG — Vector web", "EPS — Adobe legacy", "AI — Illustrator", "PDF — Print-ready", "PNG — 2048px HD", "JPG — 2048px HD", "Brand guidelines — 1-page PDF", "Social kit — profiles, banners, favicons"].map((f) => (
                    <li key={f} className="flex items-center gap-3" data-testid={`format-item-${f.split(" ")[0].toLowerCase()}`}>
                      <CircleCheck className="w-4 h-4 text-[#ED5A24]" strokeWidth={2.5} />
                      <span className="text-[#0A0A0B]">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AiLogoExample />
      <SketchShowcase />

      {/* LOST LOGO BLURB ------------------------------------------------ */}
      <section className="border-b border-black/5 bg-[#F8F9FA]" data-testid="lost-logo-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-3">
                The story
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tighter font-black leading-[1.05] mb-6">
                Lost your logo?<br />
                <span className="text-[#ED5A24]">We've seen this before.</span>
              </h2>
              <div className="w-16 h-px bg-[#0A0A0B] mb-4" />
              <p className="text-sm text-[#52525B] italic">
                Because your brand deserves better than pixels.
              </p>
            </div>

            <div className="md:col-span-7 space-y-6 text-base md:text-[17px] text-[#0A0A0B] leading-[1.7]">
              <p>
                It starts with a crisp, professional logo. Then it gets
                copied, resized, screenshotted, emailed around the office,
                downloaded from a website, and saved as{" "}
                <code className="text-[13px] font-mono px-1.5 py-0.5 bg-[#0A0A0B] text-white tracking-tight">
                  final_logo_v7_FINAL_FINAL.jpg
                </code>
                .
              </p>
              <p>
                A few years later, the original file is gone and all
                that's left is a blurry, pixelated image.
              </p>
              <p className="text-[#52525B]">
                Traditionally, you'd pay a designer to trace and redraw
                it from scratch.
              </p>
              <p className="text-lg md:text-xl font-medium tracking-tight text-[#0A0A0B] border-l-2 border-[#ED5A24] pl-5">
                Pesky Pixel brings it back to life{" "}
                <span className="bg-gradient-to-r from-[#ED5A24] to-[#1F8FED] bg-clip-text text-transparent font-bold">
                  automatically.
                </span>
              </p>
              <p>
                Upload your low-resolution logo and we'll convert it into
                clean, scalable vector artwork in minutes. No hunting for
                lost files. No expensive redraws. No more blurry logos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS --------------------------------------------------- */}
      <section className="border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-4">
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-3">01 — Workflow</div>
              <h2 className="text-3xl md:text-4xl tracking-tight font-bold leading-tight">
                Three steps. <br />Zero guesswork.
              </h2>
            </div>
            <div className="md:col-span-8 md:pt-8">
              <p className="text-base text-[#52525B] leading-relaxed">
                Our tracer combines bicubic upscaling, perceptual color quantization
                and spline path reconstruction to deliver clean geometry from even
                badly compressed source images.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            <Step n="01" title="Upload" icon={Upload}>
              Drag a flat 2D logo — PNG, JPG or WebP — onto the canvas. Pixelated,
              screenshot or compressed — all welcome.
            </Step>
            <Step n="02" title="Configure" icon={Layers}>
              Pick a color palette (2, 4, 8, 16 or full). We quantize first, trace
              second — no muddy edges.
            </Step>
            <Step n="03" title="Download" icon={Download}>
              Grab a single file or the complete vector bundle as a zip. Print,
              web, embroidery — covered.
            </Step>
          </div>
        </div>
      </section>

      {/* FEATURES TETRIS ------------------------------------------------ */}
      <section className="border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-3">02 — Engine</div>
              <h2 className="text-3xl md:text-4xl tracking-tight font-bold leading-tight mb-6">
                Built for the cases other<br />converters fumble.
              </h2>
              <p className="text-base text-[#52525B] leading-relaxed">
                Most converters choke on 200×200 PNGs from a Slack thread. We
                pre-process before tracing — antialiased edges become sharp, JPEG
                artifacts disappear, color noise collapses into clean fills.
              </p>
            </div>
            <div className="md:col-span-7 grid grid-cols-2 gap-px bg-black/10 border border-black/10">
              <Feature title="Sub-pixel precision" desc="Path coords kept at 8-digit precision." icon={ScanLine} />
              <Feature title="Smart palette" desc="Median-cut quantization preserves brand colors." icon={Layers} />
              <Feature title="Speckle filter" desc="Removes JPG noise before tracing." icon={Sparkles} />
              <Feature title="Six-format export" desc="Vector + raster bundles in one click." icon={Download} />
            </div>
          </div>
        </div>
      </section>

      <BrandExtrasSection />

      <PricingSection />
      <FaqSection />

      <CustomLogoBanner />

      {/* CTA ------------------------------------------------------------ */}
      <section className="bg-[#0A0A0B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tighter font-black leading-[0.95]">
                Stop redrawing logos.
                <br />
                <span className="text-[#FFFFFF]/40">Start </span>shipping vectors.
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to={startPath}>
                <Button
                  size="lg"
                  data-testid="footer-cta"
                  className="rounded-none bg-white text-[#0A0A0B] hover:bg-gradient-to-r hover:from-[#ED5A24] hover:to-[#1F8FED] hover:text-white text-xs uppercase tracking-[0.2em] font-bold h-12 px-8"
                >
                  {user ? "Go to dashboard" : "Create free account"} <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

function Stat({ number, label }) {
  return (
    <div>
      <div className="text-4xl font-black tracking-tighter leading-none">{number}</div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#52525B] mt-2">{label}</div>
    </div>
  );
}

function Step({ n, title, icon: Icon, children }) {
  return (
    <div className="bg-white p-8 transition-transform duration-200 hover:-translate-y-1">
      <div className="flex items-center justify-between mb-8">
        <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B]">{n}</span>
        <Icon className="w-5 h-5 text-[#ED5A24]" strokeWidth={2} />
      </div>
      <h3 className="text-xl font-medium tracking-tight mb-3">{title}</h3>
      <p className="text-sm text-[#52525B] leading-relaxed">{children}</p>
    </div>
  );
}

function Feature({ title, desc, icon: Icon }) {
  return (
    <div className="bg-white p-6 transition-colors duration-200 hover:bg-[#F8F9FA]">
      <Icon className="w-5 h-5 text-[#ED5A24] mb-4" strokeWidth={2} />
      <div className="text-sm font-bold mb-1">{title}</div>
      <div className="text-xs text-[#52525B] leading-relaxed">{desc}</div>
    </div>
  );
}
